import { BigNumber, MLString, TypeReference } from './types.js';
import { Schema } from './schema.js';
import { ExpressionConstraint, JsExpressionConstraint } from './constraint.js';
import { Option, OptionGroup, Sourceable, SourcedValueOptions } from './option.js';
import { Product, ProductGroup } from './product.js';
import { EngineNotificationsService } from './services/notifications/EngineNotificationsService.js';
import { IStructuralTransformer, TransformerResultType } from './services/transformers/StructuralTransformer.js';
import { ProductWithContext } from './dataset.js';
import { RecordKind } from './record.js';
import { ResolutionSchema, ResolutionSchemaId } from './lookup/ResolutionSchema.js';
import { CompatibilityService } from './compatibility/CompatibilityService.js';
import { optionIdSymbol, sourcesSymbol, typeSymbol } from './engine-impl.js';


export interface MutatorContext {
  readonly schema: Schema;

  mutate(model: PModel, mutator: ModelMutator): [PModel, ValidationResult];

  propertyValuesByPath(model: PModel, propertyPath: string): PropertyValues | undefined;

  propertyValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | number[] | string[] | undefined;

  compatibleOptionValuesByPath(model: PModel, propertyPath: PropertyPath): SourcedValueOptions | undefined;

  compatibleOptionValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: PropertyPath, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | undefined;

  optionValuesByPath(model: PModel, propertyPath: string): SourcedValueOptions | undefined;

  optionValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | undefined;

  validate(model: PModel): ValidationResult;

  readonly notifications: EngineNotificationsService;

  getResolutionSchema(typeRef: TypeReference): ResolutionSchema;

  compatibilityService: CompatibilityService;

}

export interface Model {
  // readonly [k: string]: Model|string|number|boolean|Array<Model|string|number|boolean>;
  readonly [k: string]: any;
}

export type PropertyValues = SourcedValueOptions | boolean[] | number[] | string[] | BigNumber[] | MLString[];

export type PModel<T = any> = {
  _id?: string;
  [typeSymbol]?: TypeReference;
  [sourcesSymbol]?: SourcesDict; // hold sources for children
  [optionIdSymbol]?: string;

  // TODO: consider fields to be readonly
} & { [K in keyof (T)]: T[K] }

export function pModelTypeGuard(value: any): value is PModel {
  return typeof (value) === 'object' && '_id' in value && typeSymbol in value && optionIdSymbol in value;
}

interface SourcesDict {
  [k: string]: Sourceable;
}

export type POption = Option;

export type POptionGroup = OptionGroup;

export function pOptionTypeGuard(value: any): value is POption {
  return typeof (value) === 'object' && '_id' in value && value.kind === RecordKind.Option;
}

export interface ModelMutator {
  mutate(context: MutatorContext, typeRef: TypeReference, model: PModel): [PModel, ValidationResult]
}

export interface ModelOptionMutator {
  mutate(context: MutatorContext, typeRef: TypeReference, model: Model, option: POption): [Model, ValidationResult]
}

export interface IOptionMutatorFactory {
  create(option: POption): ModelMutator;
}

export interface IEntitySearchResult<T> {
  path: PropertyPath;
  entity: T;
}

export interface Engine {

  /**
   * Model preparation based on Metadata and preselected prototype
   */
  init(schema: Schema, typeRef: TypeReference, prototype: {}, constraints: ExpressionConstraint[]): Model

  /**
   * Model preparation based on Metadata and preselected prototype
   */
  initByProduct(schema: Schema, product: Product): Model


  // initByProductGroup(schema: Schema, productGroup: ProductGroup): Model;


  initByProductWithContext(schema: Schema, productWithContext: ProductWithContext): PModel;

  /**
   * Validate model
   * @param model
   */
  validate(model: Model): ValidationResult

  isPropertyAvailable(propertyPath: string, model: Model): boolean

  /**
   * return corresponding POption for specified property
   * @param model
   * @param propertyPath
   */
  optionForPropertyByPath(model: Model, propertyPath: string): POption | undefined

  /**
   * Returns all available values for specified property
   * @param model
   * @param propertyPath
   */
  propertyValuesByPath(model: PModel, propertyPath: string): SourcedValueOptions | boolean[] | number[] | string[] | undefined;

  propertyValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | boolean[] | number[] | string[] | undefined;


  optionValuesByPath(model: PModel, propertyPath: string): SourcedValueOptions | undefined;

  optionValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: string, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | undefined;

  /**
   * Returns all compatible values for specified property
   * @param model - should be valid!
   * @param propertyPath
   */
  compatiblePropertyValuesByPath(model: PModel, propertyPath: PropertyPath): PropertyValues | undefined;

  compatiblePropertyValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: PropertyPath, transformer: IStructuralTransformer<T>): PropertyValues | undefined;

  compatibleOptionValuesByPath(model: PModel, propertyPath: PropertyPath): SourcedValueOptions | undefined;

  compatibleOptionValuesByPath<T extends SourcedValueOptions>(model: PModel, propertyPath: PropertyPath, transformer: IStructuralTransformer<T>): TransformerResultType<IStructuralTransformer<T>> | undefined;

  compatibleOptionsByMutator(model: Model, factory: IOptionMutatorFactory, options: POption[]): POption[];

  getLookupValues(model: PModel, lookupId: LookupId): any[] | undefined;

  /**
   * Returns compatible options for property inside POption - do we need it?
   * @param option
   * @param propertyPath
   */
  compatibleOptionsByOption(option: POption, propertyPath: string): POption[];

  /**
   * Mutate model using specified mutator and then validate it
   * @param model - should be valid!
   * @param mutator
   */
  mutate(model: Model, mutator: ModelMutator): [Model, ValidationResult]

  mutateWithOption(model: Model, option: POption, mutator: ModelOptionMutator): [Model, ValidationResult]

  addOclConstraint(oclExpression: string, errorMessage: string): void;

  addJsConstraint(model: Model, constraint: JsExpressionConstraint): ValidationResult;

  deleteConstraint(model: Model, constraint: ExpressionConstraint): any;

  entitiesByComponents<T>(model: Model, components: TypeReference[]): IEntitySearchResult<T>[];

  getLookupValue(model: PModel, lookupId: LookupId): any;

  isValueSelectedInLookup(model: PModel, value: any, lookupId: LookupId): boolean;

  readonly notifications: EngineNotificationsService;
}


export class ValidationResult {
  public get isValid() {
    return this.valid;
  }

  public get isInvalid() {
    return !this.valid;
  }

  public get errorMessage() {
    return this.message;
  }

  public static Error(message: string) {
    return new ValidationResult(false, message);
  }

  public static Valid() {
    return new ValidationResult(true, '');
  }

  private constructor(private valid: boolean, private message: string) {

  }

  and(next: () => ValidationResult): ValidationResult {
    return this.isInvalid ? this : next();
  }
}


export type PropertyPath = string;
export type LookupId = string;
